/* Amlove Memories · pet doormat customizer (one file). Built 2026-10-29 */
;
/*!
 * Amlove Memories — mat renderer v2 (shared by the storefront preview and the print service)
 * Pure drawing code: no DOM, no network. Works with a browser CanvasRenderingContext2D or a
 * Node canvas (e.g. @napi-rs/canvas) when `createCanvas(w, h)` is supplied for textures.
 *
 * A template is a list of layers drawn in order, in print pixels (e.g. 4650 x 2850).
 * Layer types: text, line, heart, paw, bone, sparkle, sprig, frame, fence, image, pets.
 * Text may contain tokens: {family} (any declared field id) and {pet1}..{pet4}.
 *
 * Modes:
 *   'mockup' — preview only: material texture, bound edge, vignette, rounded corners.
 *   'print'  — the flat production file: background colour (or transparent), artwork, text.
 *              No texture, shadow, edge or perspective.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.AmloveRenderer = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---------------- helpers ---------------- */
  function fontStr(spec, size) { return spec.replace('{size}', Math.round(size)); }

  function setFit(ctx, text, spec, maxSize, minSize, maxWidth, tracking) {
    var size = maxSize;
    ctx.font = fontStr(spec, size);
    var w = measure(ctx, text, tracking || 0);
    if (maxWidth && w > maxWidth) {
      // letter tracking is a fixed amount, so only the glyph part of the width scales with the font size
      var fixed = (tracking || 0) * Math.max(0, Array.from(text).length - 1);
      var k = Math.max(0.05, (maxWidth - fixed) / Math.max(1, w - fixed));
      size = Math.max(minSize || maxSize * 0.4, size * k);
      ctx.font = fontStr(spec, size);
    }
    return size;
  }

  function measure(ctx, text, tracking) {
    if (!tracking) return ctx.measureText(text).width;
    var chars = Array.from(text), w = 0;
    chars.forEach(function (c) { w += ctx.measureText(c).width; });
    return w + tracking * Math.max(0, chars.length - 1);
  }

  // Draw text centred (or aligned) with optional letter tracking
  function drawText(ctx, text, x, y, align, tracking) {
    if (!tracking) { ctx.textAlign = align || 'center'; ctx.fillText(text, x, y); return; }
    var total = measure(ctx, text, tracking);
    var cx = align === 'left' ? x : align === 'right' ? x - total : x - total / 2;
    ctx.textAlign = 'left';
    Array.from(text).forEach(function (c) { ctx.fillText(c, cx, y); cx += ctx.measureText(c).width + tracking; });
  }

  function drawArcText(ctx, text, arc, tracking) {
    var chars = Array.from(text);
    var total = measure(ctx, text, tracking || 0);
    var a = -Math.PI / 2 - total / arc.r / 2;
    ctx.textAlign = 'center';
    chars.forEach(function (c) {
      var w = ctx.measureText(c).width;
      var mid = a + (w / 2) / arc.r;
      ctx.save();
      ctx.translate(arc.cx + arc.r * Math.cos(mid), arc.cy + arc.r * Math.sin(mid));
      ctx.rotate(mid + Math.PI / 2);
      ctx.fillText(c, 0, 0);
      ctx.restore();
      a += (w + (tracking || 0)) / arc.r;
    });
  }

  function roundRect(ctx, x, y, w, h, r) {
    r = Math.min(r || 0, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function heartPath(ctx, cx, cy, s) {
    var h = s * 0.9;
    ctx.beginPath();
    ctx.moveTo(cx, cy - h * 0.45 + h * 0.28);
    ctx.bezierCurveTo(cx - s * 0.08, cy - h * 0.43, cx - s * 0.5, cy - h * 0.47, cx - s * 0.5, cy - h * 0.13);
    ctx.bezierCurveTo(cx - s * 0.5, cy + h * 0.15, cx - s * 0.12, cy + h * 0.33, cx, cy + h * 0.55);
    ctx.bezierCurveTo(cx + s * 0.12, cy + h * 0.33, cx + s * 0.5, cy + h * 0.15, cx + s * 0.5, cy - h * 0.13);
    ctx.bezierCurveTo(cx + s * 0.5, cy - h * 0.47, cx + s * 0.08, cy - h * 0.43, cx, cy - h * 0.45 + h * 0.28);
    ctx.closePath();
  }

  function paint(ctx, el, fallback) {
    ctx.globalAlpha = el.alpha == null ? 1 : el.alpha;
    if (el.fill) { ctx.fillStyle = el.fill === true ? (el.color || fallback) : el.fill; ctx.fill(); }
    if (el.stroke) {
      ctx.strokeStyle = el.color || fallback;
      ctx.lineWidth = el.stroke;
      ctx.lineJoin = 'round'; ctx.lineCap = 'round';
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  function withTransform(ctx, x, y, rot, fn) {
    ctx.save(); ctx.translate(x, y); if (rot) ctx.rotate(rot * Math.PI / 180); fn(); ctx.restore();
  }

  /* ---------------- decorations ---------------- */
  var DECOR = {
    heart: function (ctx, el, ink) {
      heartPath(ctx, el.x, el.y, el.size);
      paint(ctx, { fill: el.fill, stroke: el.fill ? 0 : (el.stroke || el.size * 0.14), color: el.color, alpha: el.alpha }, ink);
    },
    paw: function (ctx, el, ink) {
      var s = el.size;
      withTransform(ctx, el.x, el.y, el.rot, function () {
        ctx.fillStyle = el.color || ink; ctx.globalAlpha = el.alpha == null ? 1 : el.alpha;
        ctx.beginPath(); ctx.ellipse(0, s * 0.22, s * 0.36, s * 0.3, 0, 0, Math.PI * 2); ctx.fill();
        [[-0.42, -0.12, -20], [-0.15, -0.38, -6], [0.15, -0.38, 6], [0.42, -0.12, 20]].forEach(function (t) {
          ctx.beginPath(); ctx.ellipse(t[0] * s, t[1] * s, s * 0.13, s * 0.17, t[2] * Math.PI / 180, 0, Math.PI * 2); ctx.fill();
        });
        ctx.globalAlpha = 1;
      });
    },
    bone: function (ctx, el, ink) {
      var s = el.size;
      withTransform(ctx, el.x, el.y, el.rot, function () {
        ctx.fillStyle = el.color || ink; ctx.globalAlpha = el.alpha == null ? 1 : el.alpha;
        roundRect(ctx, -s * 0.38, -s * 0.09, s * 0.76, s * 0.18, s * 0.05); ctx.fill();
        [[-0.4, -0.1], [-0.4, 0.1], [0.4, -0.1], [0.4, 0.1]].forEach(function (k) {
          ctx.beginPath(); ctx.arc(k[0] * s, k[1] * s, s * 0.12, 0, Math.PI * 2); ctx.fill();
        });
        ctx.globalAlpha = 1;
      });
    },
    sparkle: function (ctx, el, ink) {
      var s = el.size / 2;
      withTransform(ctx, el.x, el.y, el.rot, function () {
        ctx.beginPath();
        ctx.moveTo(0, -s);
        ctx.quadraticCurveTo(s * 0.12, -s * 0.12, s, 0);
        ctx.quadraticCurveTo(s * 0.12, s * 0.12, 0, s);
        ctx.quadraticCurveTo(-s * 0.12, s * 0.12, -s, 0);
        ctx.quadraticCurveTo(-s * 0.12, -s * 0.12, 0, -s);
        ctx.closePath();
        paint(ctx, { fill: true, color: el.color, alpha: el.alpha }, ink);
      });
    },
    sprig: function (ctx, el, ink) {
      var L = el.size;
      withTransform(ctx, el.x, el.y, el.rot, function () {
        if (el.flip) ctx.scale(1, -1);
        ctx.strokeStyle = el.color || ink; ctx.fillStyle = el.color || ink;
        ctx.globalAlpha = el.alpha == null ? 1 : el.alpha;
        ctx.lineWidth = L * 0.022; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(L * 0.5, -L * 0.12, L, 0); ctx.stroke();
        for (var i = 1; i <= 5; i++) {
          var t = i / 6, px = L * t, py = -L * 0.12 * 4 * t * (1 - t) * 0.5;
          var lw = L * (0.2 - t * 0.08);
          [-1, 1].forEach(function (side) {
            ctx.save(); ctx.translate(px, py); ctx.rotate(side * 0.75 - 0.15);
            ctx.beginPath(); ctx.ellipse(lw * 0.5, 0, lw * 0.5, lw * 0.2, 0, 0, Math.PI * 2); ctx.fill();
            ctx.restore();
          });
        }
        ctx.beginPath(); ctx.ellipse(L * 1.04, 0, L * 0.07, L * 0.035, 0, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
      });
    },
    line: function (ctx, el, ink) {
      ctx.save();
      ctx.beginPath(); ctx.moveTo(el.x1, el.y1); ctx.lineTo(el.x2, el.y2 == null ? el.y1 : el.y2);
      ctx.lineCap = el.cap || 'round';
      if (el.dash) ctx.setLineDash(el.dash);
      ctx.lineWidth = el.width; ctx.strokeStyle = el.color || ink; ctx.globalAlpha = el.alpha == null ? 1 : el.alpha;
      ctx.stroke();
      ctx.restore();
    },
    frame: function (ctx, el, ink, T) {
      ctx.save();
      var ix = el.insetX != null ? el.insetX : el.inset, iy = el.insetY != null ? el.insetY : el.inset;
      roundRect(ctx, ix, iy, T.size.w - 2 * ix, T.size.h - 2 * iy, el.radius || 0);
      if (el.dash) ctx.setLineDash(el.dash);
      ctx.lineCap = 'round';
      ctx.lineWidth = el.width; ctx.strokeStyle = el.color || ink; ctx.globalAlpha = el.alpha == null ? 1 : el.alpha;
      ctx.stroke();
      ctx.restore();
    },
    image: function (ctx, el, ink, T, opts) {
      var img = opts && opts.images && opts.images[el.asset];
      withTransform(ctx, el.x, el.y, el.rot, function () {
        if (el.flipX) ctx.scale(-1, 1);
        ctx.globalAlpha = el.alpha == null ? 1 : el.alpha;
        if (img) ctx.drawImage(img, -el.w / 2, -el.h / 2, el.w, el.h);
        else { ctx.setLineDash([30, 20]); ctx.lineWidth = 8; ctx.strokeStyle = 'rgba(128,128,128,.6)'; ctx.strokeRect(-el.w / 2, -el.h / 2, el.w, el.h); }
        ctx.globalAlpha = 1;
      });
    },
    fence: function (ctx, el) {
      ctx.save();
      ctx.lineJoin = 'round';
      // rails behind the pickets
      (el.rails || []).forEach(function (ry) {
        ctx.fillStyle = el.fill; ctx.strokeStyle = el.color; ctx.lineWidth = el.stroke;
        ctx.fillRect(el.x1, ry, el.x2 - el.x1, el.railH); ctx.strokeRect(el.x1, ry, el.x2 - el.x1, el.railH);
      });
      var n = Math.floor((el.x2 - el.x1 + el.gap) / (el.picketW + el.gap));
      var used = n * el.picketW + (n - 1) * el.gap, x = el.x1 + (el.x2 - el.x1 - used) / 2;
      for (var i = 0; i < n; i++, x += el.picketW + el.gap) {
        var pw = el.picketW, tip = pw * 0.55;
        ctx.beginPath();
        ctx.moveTo(x, el.y + tip); ctx.lineTo(x + pw / 2, el.y); ctx.lineTo(x + pw, el.y + tip);
        ctx.lineTo(x + pw, el.y + el.h); ctx.lineTo(x, el.y + el.h); ctx.closePath();
        ctx.fillStyle = el.fill; ctx.fill();
        ctx.strokeStyle = el.color; ctx.lineWidth = el.stroke; ctx.stroke();
      }
      ctx.restore();
    }
  };

  /* ---------------- tokens ---------------- */
  function resolve(text, design) {
    var missing = false;
    var out = text.replace(/\{(\w+)\}/g, function (_, k) {
      var m = /^pet(\d)$/.exec(k), v;
      if (m) v = design.pets[+m[1] - 1] && design.pets[+m[1] - 1].name;
      else v = design.fields && design.fields[k];
      v = (v || '').trim();
      if (!v) missing = true;
      return v;
    });
    return { text: out, missing: missing };
  }

  /* ---------------- pets layout ---------------- */
  function layoutPets(T, layer, design, catalog) {
    var pxScale = catalog.pxScale || 1;
    var lw = layer.line ? layer.line.width : 0;
    var pets = layer.slots.map(function (slot, i) {
      var pet = design.pets[i] || {};
      var meta = catalog.assets[pet.asset];
      var w = 0, h = 0, core = (meta && meta.core) || [0, 1];
      if (meta) {
        w = meta.w * pxScale * (slot.scale || 1);
        h = meta.h * pxScale * (slot.scale || 1);
        // fit by the body width (core), ignoring thin whiskers; shrink only — never stretch or distort
        var fit = Math.min(1, slot.maxW / (w * core[1]), slot.maxH / h);
        w *= fit; h *= fit;
      }
      // sink: share of the pet's height below its solid base (rounded paw tips) — those overlap the line
      var by = layer.baseline + lw * 0.5 + (meta && meta.sink ? h * meta.sink : 0);
      return { index: i, asset: pet.asset, name: (pet.name || '').trim(), cx: slot.x, x: slot.x - (core[0] + core[1] / 2) * w, y: by - h, w: w, h: h, slot: slot };
    });
    var line = null;
    if (layer.line) {
      var L = layer.line;
      if (L.x1 != null) line = { x1: L.x1, x2: L.x2 };
      else {
        var left = Math.min.apply(null, pets.map(function (p) { return p.x; }));
        var right = Math.max.apply(null, pets.map(function (p) { return p.x + p.w; }));
        var mid = T.size.w / 2;
        var half = Math.max(L.minHalf || 0, mid - left + L.overhang, right - mid + L.overhang);
        half = Math.min(half, mid - (T.safe || 0) - 60);
        line = { x1: mid - half, x2: mid + half };
      }
      line.y = layer.baseline; line.width = L.width; line.color = L.color;
    }
    return { pets: pets, line: line };
  }

  /* ---------------- material (mock-up only) ---------------- */
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  var tileCache = {};
  function materialTile(createCanvas, bg, px) {
    var key = bg.texture + bg.color + px;
    if (tileCache[key]) return tileCache[key];
    var c = createCanvas(px, px), g = c.getContext('2d'), rnd = mulberry32(20261028);
    g.fillStyle = bg.color; g.fillRect(0, 0, px, px);
    var light = bg.fiberLight || 'rgba(255,255,255,0.5)', dark = bg.fiberDark || 'rgba(0,0,0,0.5)';
    if (bg.texture === 'coir') {
      var cell = Math.max(3, px / 48);
      for (var y = 0; y < px; y += cell) for (var x = 0; x < px; x += cell) {
        g.globalAlpha = 0.04 + rnd() * 0.07;
        g.fillStyle = (((x / cell | 0) + (y / cell | 0)) % 2) ? dark : light;
        g.fillRect(x, y, cell, cell * 0.92);
      }
      var count = Math.round(px * px / 45);
      g.lineCap = 'round';
      for (var i = 0; i < count; i++) {
        var fx = rnd() * px, fy = rnd() * px, len = cell * (0.5 + rnd() * 1.6);
        var ang = (rnd() < 0.5 ? 0 : Math.PI / 2) + (rnd() - 0.5) * 0.7;
        g.globalAlpha = 0.18 + rnd() * 0.3;
        g.strokeStyle = rnd() < 0.55 ? light : dark;
        g.lineWidth = Math.max(0.6, cell * (0.08 + rnd() * 0.14));
        g.beginPath(); g.moveTo(fx, fy); g.lineTo(fx + Math.cos(ang) * len, fy + Math.sin(ang) * len); g.stroke();
      }
    } else { // 'felt' — fine, soft speckle for printed polyester mats
      var dots = Math.round(px * px / 12);
      for (var j = 0; j < dots; j++) {
        g.globalAlpha = 0.05 + rnd() * 0.08;
        g.fillStyle = rnd() < 0.5 ? light : dark;
        var r = Math.max(0.5, px / 400 * (0.5 + rnd()));
        g.fillRect(rnd() * px, rnd() * px, r, r);
      }
    }
    g.globalAlpha = 1;
    tileCache[key] = c;
    return c;
  }

  /* ---------------- experimental: light outline (sticker edge) ---------------- */
  var outlineCache = {};
  function drawOutline(ctx, img, p, o, scale, createCanvas, baselineY) {
    var rd = Math.max(1, o.width * scale), pad = Math.ceil(rd) + 2;
    var dw = Math.round(p.w * scale), dh = Math.round(p.h * scale);
    var key = p.asset + '|' + o.width + '|' + o.color + '|' + dw;
    var out = outlineCache[key];
    if (!out) {
      var sil = createCanvas(dw + 2 * pad, dh + 2 * pad), sg = sil.getContext('2d');
      sg.drawImage(img, pad, pad, dw, dh);
      sg.globalCompositeOperation = 'source-in'; sg.fillStyle = o.color; sg.fillRect(0, 0, sil.width, sil.height);
      out = createCanvas(sil.width, sil.height);
      var og = out.getContext('2d'), steps = 32;
      for (var i = 0; i < steps; i++) {
        var a = i / steps * Math.PI * 2;
        og.drawImage(sil, Math.cos(a) * rd, Math.sin(a) * rd);
      }
      og.drawImage(sil, 0, 0);
      outlineCache[key] = out;
    }
    ctx.save();
    ctx.beginPath(); ctx.rect(p.x - pad / scale, p.y - pad / scale, p.w + 2 * pad / scale, baselineY - (p.y - pad / scale)); ctx.clip();
    ctx.globalAlpha = o.alpha == null ? 1 : o.alpha;
    ctx.drawImage(out, p.x - pad / scale, p.y - pad / scale, out.width / scale, out.height / scale);
    ctx.restore();
  }

  /* ---------------- main entry ---------------- */
  function render(ctx, T, design, catalog, opts) {
    opts = opts || {};
    var scale = opts.scale || 1, mode = opts.mode || 'mockup';
    var W = T.size.w, H = T.size.h, bg = T.background, ink = T.ink;
    var result = { pets: [], line: null };

    // Mock-ups show only the finished rug (print area minus bleed); print files use the whole canvas.
    var vb = viewBox(T, mode);
    ctx.save();
    ctx.setTransform(scale, 0, 0, scale, -vb.x * scale, -vb.y * scale);
    ctx.clearRect(vb.x, vb.y, vb.w, vb.h);
    ctx.imageSmoothingEnabled = true;
    if ('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
    ctx.textBaseline = 'alphabetic';

    if (mode === 'mockup') {
      var radius = (bg.edge && bg.edge.radius) || 50;
      roundRect(ctx, vb.x, vb.y, vb.w, vb.h, radius);
      ctx.save(); ctx.clip();
      if (bg.texture && opts.createCanvas) {
        var tile = materialTile(opts.createCanvas, bg, Math.max(48, Math.round(520 * scale)));
        ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.fillStyle = ctx.createPattern(tile, 'repeat'); ctx.fillRect(0, 0, vb.w * scale, vb.h * scale);
        ctx.restore();
      } else { ctx.fillStyle = bg.color; ctx.fillRect(vb.x, vb.y, vb.w, vb.h); }
      drawBgImage(ctx, T, opts);
      var vg = ctx.createRadialGradient(W / 2, H / 2, vb.h * 0.35, W / 2, H / 2, vb.w * 0.62);
      vg.addColorStop(0, 'rgba(255,245,230,0.06)'); vg.addColorStop(1, 'rgba(30,18,8,0.20)');
      ctx.fillStyle = vg; ctx.fillRect(vb.x, vb.y, vb.w, vb.h);
      ctx.restore();
      if (bg.edge) {
        var ew = bg.edge.width;
        ctx.strokeStyle = bg.edge.color; ctx.lineWidth = ew;
        roundRect(ctx, vb.x + ew / 2, vb.y + ew / 2, vb.w - ew, vb.h - ew, radius);
        ctx.stroke();
      }
    } else if (bg.print !== 'transparent') {
      ctx.fillStyle = bg.color; ctx.fillRect(0, 0, W, H); // flat, edge to edge (covers the bleed too)
      drawBgImage(ctx, T, opts);
    }

    T.layers.forEach(function (el) {
      if (el.hidden) return;
      ctx.save();
      if (el.type === 'text') {
        var r = resolve(el.text, design);
        if (r.missing && el.optional) { ctx.restore(); return; }
        var txt = el.upper ? r.text.toUpperCase() : r.text;
        ctx.fillStyle = el.color || ink;
        setFit(ctx, txt, el.font, el.size, el.minSize, el.maxWidth, el.tracking);
        if (el.arc) drawArcText(ctx, txt, el.arc, el.tracking);
        else if (el.rot) withTransform(ctx, el.x, el.y, el.rot, function () { drawText(ctx, txt, 0, 0, el.align, el.tracking); });
        else drawText(ctx, txt, el.x, el.y, el.align, el.tracking);
        if (el.sideLines) {
          var tw = measure(ctx, txt, el.tracking), s = el.sideLines;
          DECOR.line(ctx, { x1: el.x - tw / 2 - s.gap - s.len, x2: el.x - tw / 2 - s.gap, y1: el.y + s.dy, width: s.width, color: s.color || el.color }, ink);
          DECOR.line(ctx, { x1: el.x + tw / 2 + s.gap, x2: el.x + tw / 2 + s.gap + s.len, y1: el.y + s.dy, width: s.width, color: s.color || el.color }, ink);
        }
      } else if (el.type === 'pets') {
        var lay = layoutPets(T, el, design, catalog);
        result = lay;
        if (lay.line) DECOR.line(ctx, { x1: lay.line.x1, x2: lay.line.x2, y1: lay.line.y, width: lay.line.width, color: lay.line.color }, ink);
        var outline = opts.petOutline || el.outline || null; // light 'sticker' outline, set per template (e.g. Midnight Solo)
        lay.pets.forEach(function (p) {
          var img = opts.images && opts.images[p.asset];
          if (img && p.w && outline && opts.createCanvas) drawOutline(ctx, img, p, outline, scale, opts.createCanvas, lay.line ? lay.line.y : el.baseline);
          if (img && p.w) ctx.drawImage(img, p.x, p.y, p.w, p.h);
          else if (p.slot) { // loading placeholder (preview only)
            ctx.save(); ctx.globalAlpha = 0.15; ctx.fillStyle = el.names.color || ink;
            var pw = p.slot.maxW * 0.7, ph = p.slot.maxH * 0.8;
            roundRect(ctx, p.cx - pw / 2, el.baseline - ph, pw, ph, pw * 0.35); ctx.fill(); ctx.restore();
          }
          if (opts.highlight === p.index && lay.pets.length > 1 && p.w) {
            ctx.save(); ctx.setLineDash([28, 22]); ctx.lineWidth = 10; ctx.strokeStyle = opts.highlightColor || 'rgba(255,255,255,.85)';
            roundRect(ctx, p.x - 30, p.y - 30, p.w + 60, p.h + 30, 60); ctx.stroke(); ctx.restore();
          }
        });
        var N = el.names;
        lay.pets.forEach(function (p) {
          var name = p.name, ghost = false;
          if (!name && opts.placeholderName) { name = opts.placeholderName; ghost = true; }
          if (name) {
            ctx.save();
            if (ghost) ctx.globalAlpha = 0.35;
            ctx.fillStyle = N.color || ink;
            var nm = N.upper ? name.toUpperCase() : name;
            setFit(ctx, nm, N.font, N.size, N.minSize, p.slot.nameW || N.maxWidth, N.tracking);
            drawText(ctx, nm, p.cx, N.y, 'center', N.tracking);
            ctx.restore();
          }
          if (N.deco) {
            var d = Object.assign({}, N.deco, { x: p.cx, y: N.y + N.deco.dy });
            (DECOR[d.kind] || DECOR.heart)(ctx, d, ink);
          }
        });
      } else if (DECOR[el.type]) {
        DECOR[el.type](ctx, el, ink, T, opts);
      }
      ctx.restore();
    });

    if (opts.guides && T.bleed) drawGuides(ctx, T);
    ctx.restore();
    return result;
  }

  /** Optional full-bleed background image, scaled to cover the whole canvas without distortion. */
  function drawBgImage(ctx, T, opts) {
    var b = T.background, img = b.image && opts.images && opts.images[b.image];
    if (!img) return;
    var W = T.size.w, H = T.size.h, iw = img.width || img.naturalWidth, ih = img.height || img.naturalHeight;
    var k = Math.max(W / iw, H / ih), dw = iw * k, dh = ih * k;
    ctx.save(); ctx.globalAlpha = b.imageAlpha == null ? 1 : b.imageAlpha;
    ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh); ctx.restore();
  }

  /** Measured text for a text layer (used by the editor for selection boxes and checks). */
  function textInfo(ctx, el, design) {
    var r = resolve(el.text, design), txt = el.upper ? r.text.toUpperCase() : r.text;
    ctx.save();
    var size = setFit(ctx, txt, el.font, el.size, el.minSize, el.maxWidth, el.tracking);
    var width = measure(ctx, txt, el.tracking);
    var minFit = true;
    if (el.maxWidth && width > el.maxWidth + 1) minFit = false;
    ctx.restore();
    return { text: txt, size: size, width: width, missing: r.missing, fits: minFit };
  }

  /** Visible box: the whole canvas for print files, the finished rug (minus bleed) for mock-ups. */
  function viewBox(T, mode) {
    var b = T.bleed || { x: 0, y: 0 };
    return mode === 'mockup' ? { x: b.x, y: b.y, w: T.size.w - 2 * b.x, h: T.size.h - 2 * b.y } : { x: 0, y: 0, w: T.size.w, h: T.size.h };
  }

  /** Review overlay: bleed (cut/hem) in red, safe zone in green. Never part of a production file. */
  function drawGuides(ctx, T) {
    var W = T.size.w, H = T.size.h, b = T.bleed, s = T.safeInset;
    ctx.save();
    ctx.fillStyle = 'rgba(220,40,40,0.28)';
    ctx.fillRect(0, 0, W, b.y); ctx.fillRect(0, H - b.y, W, b.y); ctx.fillRect(0, b.y, b.x, H - 2 * b.y); ctx.fillRect(W - b.x, b.y, b.x, H - 2 * b.y);
    ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(220,40,40,0.95)'; ctx.strokeRect(b.x, b.y, W - 2 * b.x, H - 2 * b.y);
    ctx.setLineDash([40, 26]); ctx.strokeStyle = 'rgba(20,170,80,0.95)'; ctx.lineWidth = 8;
    ctx.strokeRect(s.x, s.y, W - 2 * s.x, H - 2 * s.y);
    ctx.restore();
  }

  /** Every CSS font shorthand the template uses (for document.fonts.load or server font registration). */
  function requiredFonts(T) {
    var out = [];
    T.layers.forEach(function (el) {
      if (el.type === 'text') out.push(fontStr(el.font, 100));
      if (el.type === 'pets') out.push(fontStr(el.names.font, 100));
    });
    return out.filter(function (f, i) { return out.indexOf(f) === i; });
  }

  function petsLayer(T) { return T.layers.filter(function (l) { return l.type === 'pets'; })[0]; }

  return { render: render, requiredFonts: requiredFonts, petsLayer: petsLayer, resolve: resolve, viewBox: viewBox,
    textInfo: textInfo, layoutPets: layoutPets, fontStr: fontStr, setFit: setFit, measure: measure };
});

;
/*!
 * Amlove Memories — design model & validation (shared by browser and print service)
 *
 * The browser uses it to build the cart payload and give instant feedback.
 * The server uses the SAME rules to re-validate every paid order against its own copy of the
 * authorized templates and catalog. The server never trusts names, labels or keys sent by the browser:
 * it rebuilds the canonical design from IDs, re-derives the design key and the visible labels.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.AmloveDesign = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SCHEMA = 2;
  var TEXT_RE = /^[\p{L}\p{M}0-9 .'’&-]*$/u;
  var AUTHORIZED = { approved: true, draft: true }; // server config: in production only "approved"

  function clean(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); }
  function len(s) { return Array.from(s).length; }

  function indexTemplates(templatesDoc) {
    var idx = {};
    templatesDoc.templates.forEach(function (t) { idx[t.id + '@' + t.rev] = t; });
    return idx;
  }

  function assetIndex(catalog) {
    var idx = {};
    catalog.species.forEach(function (sp) {
      sp.breeds.forEach(function (b) {
        b.variants.forEach(function (v) { idx[v.asset] = { species: sp, breed: b, variant: v }; });
      });
    });
    return idx;
  }

  /** Canonical design object: IDs and customer text only. Nothing derived, nothing visual. */
  function build(template, catalog, pets, fieldValues) {
    var fields = {};
    (template.fields || []).forEach(function (f) { fields[f.id] = clean(fieldValues && fieldValues[f.id]); });
    return {
      schema: SCHEMA,
      template: template.id + '@' + template.rev,
      catalog: catalog.catalogVersion,
      pets: pets.slice(0, template.petCount).map(function (p) { return { asset: p.asset, name: clean(p.name) }; }),
      fields: fields
    };
  }

  /** Stable JSON (sorted keys) so browser and server hash exactly the same bytes. */
  function canonicalJSON(v) {
    if (Array.isArray(v)) return '[' + v.map(canonicalJSON).join(',') + ']';
    if (v && typeof v === 'object') {
      return '{' + Object.keys(v).sort().map(function (k) { return JSON.stringify(k) + ':' + canonicalJSON(v[k]); }).join(',') + '}';
    }
    return JSON.stringify(v);
  }

  function keyFromBytes(bytes) {
    var A = '23456789ABCDEFGHJKMNPQRSTUVWXYZ', out = '';
    for (var i = 0; i < 8; i++) out += A[bytes[i] % A.length];
    return 'AM-' + out.slice(0, 4) + '-' + out.slice(4);
  }

  /** Short human-friendly fingerprint of the design (SHA-256). Returns a Promise. */
  function designKey(design) {
    var data = canonicalJSON(design);
    if (typeof require === 'function' && typeof window === 'undefined') {
      var h = require('crypto').createHash('sha256').update(data, 'utf8').digest();
      return Promise.resolve(keyFromBytes(h));
    }
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(data))
      .then(function (buf) { return keyFromBytes(new Uint8Array(buf)); });
  }

  /**
   * Validate a design. ctx = { templates: indexTemplates(...), catalog, productHandle?, authorized? }
   * Returns { ok, errors: [{code, path, message}], template }
   */
  function validate(design, ctx) {
    var errors = [];
    function err(code, path, message) { errors.push({ code: code, path: path, message: message }); }
    if (!design || typeof design !== 'object') { err('shape', '', 'Design is missing.'); return { ok: false, errors: errors }; }
    if (design.schema !== SCHEMA) err('schema', 'schema', 'Unknown design schema.');

    var T = ctx.templates[design.template];
    var authorized = ctx.authorized || AUTHORIZED;
    if (!T) { err('template', 'template', 'Template "' + design.template + '" is not an authorized template.'); return { ok: false, errors: errors }; }
    if (!authorized[T.status]) err('template_status', 'template', 'Template "' + design.template + '" is not approved for sale.');
    if (ctx.productHandle && T.product.handle !== ctx.productHandle) err('product', 'template', 'Template does not belong to product "' + ctx.productHandle + '".');
    if (ctx.catalog && design.catalog !== ctx.catalog.catalogVersion && !(ctx.knownCatalogs || {})[design.catalog]) err('catalog', 'catalog', 'Unknown illustration catalog version.');

    var assets = assetIndex(ctx.catalog);
    if (!Array.isArray(design.pets) || design.pets.length !== T.petCount) {
      err('pet_count', 'pets', 'This design needs exactly ' + T.petCount + (T.petCount === 1 ? ' pet.' : ' pets.'));
    }
    (design.pets || []).slice(0, 8).forEach(function (p, i) {
      var path = 'pets[' + i + ']';
      if (!p || !assets[p.asset]) err('asset', path + '.asset', 'Pet ' + (i + 1) + ': unknown illustration.');
      var n = clean(p && p.name);
      if (!n) err('name_required', path + '.name', 'Add a name for Pet ' + (i + 1) + '.');
      else if (len(n) > (T.nameMax || 12)) err('name_length', path + '.name', 'Pet ' + (i + 1) + ': names can have up to ' + (T.nameMax || 12) + ' characters.');
      else if (!TEXT_RE.test(n)) err('name_chars', path + '.name', 'Pet ' + (i + 1) + ': use letters, numbers, spaces and - \' . & only.');
      if (p && p.name !== n) err('name_unclean', path + '.name', 'Pet ' + (i + 1) + ': name has extra spaces.');
    });

    var declared = {};
    (T.fields || []).forEach(function (f) {
      declared[f.id] = true;
      var v = clean(design.fields && design.fields[f.id]);
      if (f.required && !v) err('field_required', 'fields.' + f.id, f.label + ' is required.');
      if (len(v) > f.max) err('field_length', 'fields.' + f.id, f.label + ': up to ' + f.max + ' characters.');
      if (!TEXT_RE.test(v)) err('field_chars', 'fields.' + f.id, f.label + ': use letters, numbers, spaces and - \' . & only.');
    });
    Object.keys(design.fields || {}).forEach(function (k) {
      if (!declared[k]) err('field_unknown', 'fields.' + k, 'Field "' + k + '" is not part of this design.');
    });

    return { ok: errors.length === 0, errors: errors, template: T };
  }

  /** Labels for the cart / order, always derived from IDs (never taken from the browser). */
  function displayProperties(design, T, catalog) {
    var assets = assetIndex(catalog), props = {};
    design.pets.forEach(function (p, i) {
      var a = assets[p.asset];
      props['Pet ' + (i + 1)] = p.name + ' · ' + (a ? a.breed.label + ' · ' + a.variant.label + ' (' + a.species.label + ')' : '?');
    });
    (T.fields || []).forEach(function (f) {
      var v = design.fields[f.id];
      props[f.label] = v ? v : '(none)';
    });
    props['Design'] = T.product.title;
    return props;
  }

  /** Shopify line item properties. "_" keys are hidden from shoppers at checkout. */
  function lineItemProperties(design, T, catalog, key) {
    var props = displayProperties(design, T, catalog);
    props._design = canonicalJSON(design);
    props._design_key = key;
    return props;
  }

  /** Image ids a template draws by itself (decorations / background), not the customer's pets. */
  function templateAssets(T) {
    var out = [];
    (T.layers || []).forEach(function (l) { if (l.type === 'image' && l.asset && out.indexOf(l.asset) < 0) out.push(l.asset); });
    if (T.background && T.background.image && out.indexOf(T.background.image) < 0) out.push(T.background.image);
    return out;
  }

  return {
    SCHEMA: SCHEMA, TEXT_RE: TEXT_RE, clean: clean, templateAssets: templateAssets,
    indexTemplates: indexTemplates, assetIndex: assetIndex,
    build: build, canonicalJSON: canonicalJSON, designKey: designKey,
    validate: validate, displayProperties: displayProperties, lineItemProperties: lineItemProperties
  };
});

;
/*!
 * Amlove Memories — customizer v2 (one component for every mat design)
 *   AmloveStore     shared pet list + text fields, remembered per shopper (localStorage, optional)
 *   AmloveImages    shared illustration loader/cache
 *   AmlovePreview   draws any template into a <canvas> at screen resolution
 *   AmloveCustomizer.mount(rootEl, { template, catalog, store, images, previewCanvas, cart, price, variantId })
 * Depends on AmloveRenderer + AmloveDesign. No framework. Customer text is never inserted as HTML.
 */
(function (root) {
  'use strict';
  var R = root.AmloveRenderer, D = root.AmloveDesign;
  var MAX_POOL = 4;

  function h(tag, attrs, kids) {
    var el = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v == null || v === false) return;
      if (k === 'class') el.className = v;
      else if (k === 'text') el.textContent = v;
      else if (k.slice(0, 2) === 'on') el.addEventListener(k.slice(2), v);
      else if (v === true) el.setAttribute(k, '');
      else el.setAttribute(k, v);
    });
    (kids || []).forEach(function (c) { if (c) el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return el;
  }
  var seq = 0;
  function uid(p) { seq += 1; return 'amc-' + p + '-' + seq; }
  function makeCanvas(w, hh) { var c = document.createElement('canvas'); c.width = w; c.height = hh; return c; }

  /* =========================== catalog helpers =========================== */
  function Lib(catalog) {
    this.catalog = catalog;
    this.byAsset = D.assetIndex(catalog);
  }
  Lib.prototype = {
    species: function (id) { return this.catalog.species.filter(function (s) { return s.id === id; })[0]; },
    breed: function (sp, id) { var s = this.species(sp); return s && s.breeds.filter(function (b) { return b.id === id; })[0]; },
    asset: function (sp, b, v) { var br = this.breed(sp, b), x = br && br.variants.filter(function (q) { return q.id === v; })[0]; return x && x.asset; },
    // catalog pets live next to the script (base + file); Studio decorations carry their own URL (T.libAssets)
    src: function (base, assetId) { var a = this.catalog.assets[assetId]; if (a) return base + a.file; var x = this.extra && this.extra[assetId]; return x ? (x.url || x) : ''; },
    describe: function (assetId) { var a = this.byAsset[assetId]; return a ? { species: a.species.label, breed: a.breed.label, color: a.variant.label } : null; }
  };

  /* =========================== shared store =========================== */
  var STORE_KEY = 'amlove.pets.v2';
  function Store(lib, defaults) {
    this.lib = lib;
    this.subs = [];
    var saved = this._load();
    this.pets = saved ? saved.pets : defaults.pets.map(this._normalize, this);
    this.fields = saved ? saved.fields : Object.assign({}, defaults.fields);
    while (this.pets.length < MAX_POOL) this.pets.push(this._normalize(defaults.pets[this.pets.length % defaults.pets.length]));
  }
  Store.prototype = {
    _normalize: function (p) {
      var q = { species: p.species, breed: p.breed, variant: p.variant, name: D.clean(p.name).slice(0, 16), memory: p.memory || {} };
      q.memory[q.species] = { breed: q.breed, variant: q.variant };
      return q;
    },
    _load: function () {
      try {
        var s = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'), lib = this.lib;
        if (!s || !Array.isArray(s.pets)) return null;
        var pets = s.pets.slice(0, MAX_POOL).filter(function (p) { return lib.asset(p.species, p.breed, p.variant); }).map(this._normalize, this);
        if (!pets.length) return null;
        var fields = {};
        Object.keys(s.fields || {}).forEach(function (k) { fields[k] = D.clean(s.fields[k]).slice(0, 24); });
        return { pets: pets, fields: fields };
      } catch (e) { return null; }
    },
    save: function () {
      try { localStorage.setItem(STORE_KEY, JSON.stringify({ pets: this.pets, fields: this.fields })); } catch (e) { /* storage blocked: draft not remembered */ }
    },
    reset: function () { try { localStorage.removeItem(STORE_KEY); } catch (e) {} },
    on: function (fn) { this.subs.push(fn); },
    changed: function (src) { var self = this; this.save(); this.subs.forEach(function (fn) { fn(self, src); }); },
    assetOf: function (i) { var p = this.pets[i]; return this.lib.asset(p.species, p.breed, p.variant); },
    designFor: function (T) {
      var self = this;
      var pets = this.pets.slice(0, T.petCount).map(function (p, i) { return { asset: self.assetOf(i), name: p.name }; });
      return D.build(T, this.lib.catalog, pets, this.fields);
    }
  };

  /* =========================== images =========================== */
  function Images(lib, base) { this.lib = lib; this.base = base; this.map = {}; this.pending = {}; this.subs = []; }
  Images.prototype = {
    on: function (fn) { this.subs.push(fn); },
    ensure: function (assetIds) {
      var self = this;
      assetIds.forEach(function (a) {
        if (!a || self.map[a] || self.pending[a]) return;
        var img = new Image();
        self.pending[a] = true;
        img.decoding = 'async';
        img.onload = function () { self.map[a] = img; delete self.pending[a]; self.subs.forEach(function (fn) { fn(a); }); };
        img.onerror = function () { delete self.pending[a]; };
        img.src = self.lib.src(self.base, a);
      });
    }
  };

  /* =========================== preview =========================== */
  var Preview = {
    draw: function (canvas, T, design, catalog, images, opts) {
      opts = opts || {};
      var cssW = canvas.clientWidth || opts.width || 600;
      var dpr = Math.min(root.devicePixelRatio || 1, opts.maxDpr || 2.5);
      var vb = R.viewBox(T, 'mockup');
      var pw = Math.round(cssW * dpr), ph = Math.round(pw * vb.h / vb.w);
      if (canvas.width !== pw || canvas.height !== ph) { canvas.width = pw; canvas.height = ph; }
      canvas.style.aspectRatio = vb.w + ' / ' + vb.h;
      return R.render(canvas.getContext('2d'), T, design, catalog, Object.assign({
        scale: pw / vb.w, mode: 'mockup', images: images.map, createCanvas: makeCanvas
      }, opts));
    },
    loadFonts: function (templates) {
      if (!document.fonts || !document.fonts.load) return Promise.resolve();
      var all = [];
      templates.forEach(function (T) { R.requiredFonts(T).forEach(function (f) { if (all.indexOf(f) < 0) all.push(f); }); });
      return Promise.all(all.map(function (f) { return document.fonts.load(f).catch(function () {}); }));
    }
  };

  /* =========================== customizer =========================== */
  function Customizer(rootEl, o) {
    this.root = rootEl;
    this.o = o;
    this.T = o.template;
    this.lib = o.lib;
    this.store = o.store;
    this.images = o.images;
    this.canvas = o.previewCanvas;
    this.active = 0;
    this.breedOpen = false;
    this.highlight = null;
    this.listeners = [];
    var self = this;
    this._onStore = function (s, src) { if (src !== self) { self._renderEditor(); self._renderFields(); } self.refresh(); };
    this._onImg = function () { self._schedule(); };
    this.store.on(this._onStore);
    this.images.on(this._onImg);
    this._build();
    this._bindPreview();
    this.refresh();
  }

  Customizer.prototype = {
    pet: function () { return this.store.pets[this.active]; },
    getDesign: function () { return this.store.designFor(this.T); },
    on: function (fn) { this.listeners.push(fn); },
    validate: function () { return D.validate(this.getDesign(), { templates: this.o.templateIndex, catalog: this.lib.catalog }); },

    _changed: function (opts) {
      opts = opts || {};
      if (opts.rebuild) this._renderEditor();
      this.store.changed(this);
      if (opts.flash != null) this._flash(opts.flash);
    },
    selectPet: function (i) {
      if (i === this.active) return;
      this.active = i; this.breedOpen = false;
      this._renderEditor(); this.refresh(); this._flash(i);
    },
    movePet: function (i, dir) {
      var j = i + dir, pets = this.store.pets;
      if (j < 0 || j >= this.T.petCount) return;
      var t = pets[i]; pets[i] = pets[j]; pets[j] = t;
      this.active = j;
      this._changed({ rebuild: true, flash: j });
      this._focus('[data-amc="move-' + (dir < 0 ? 'left' : 'right') + '"]');
    },
    setSpecies: function (sp) {
      var p = this.pet();
      if (p.species === sp) return;
      p.memory[p.species] = { breed: p.breed, variant: p.variant };
      var m = p.memory[sp];
      if (!m) {
        var br = sp === 'cat' ? this.lib.breed('cat', 'domestic-cat') : this.lib.species(sp).breeds[0];
        m = { breed: br.id, variant: br.variants[0].id };
        this.breedOpen = true;
      }
      p.species = sp; p.breed = m.breed; p.variant = m.variant; // the name stays
      this._changed({ rebuild: true, flash: this.active });
    },
    setBreed: function (b) {
      var p = this.pet(), br = this.lib.breed(p.species, b);
      var keep = br.variants.filter(function (v) { return v.id === p.variant; })[0];
      p.breed = b; p.variant = (keep || br.variants[0]).id;
      p.memory[p.species] = { breed: p.breed, variant: p.variant };
      this.breedOpen = false;
      this._changed({ rebuild: true, flash: this.active });
      this._focus('[data-amc="breed-toggle"]');
      this._announce(br.label + ' selected' + (br.variants.length > 1 ? '. Choose a color next.' : '.'));
    },
    setVariant: function (v) {
      var p = this.pet();
      p.variant = v; p.memory[p.species] = { breed: p.breed, variant: v };
      var img = this.editor.querySelector('.amc-breed-current img');
      if (img) img.src = this.lib.src(this.o.assetBase, this.store.assetOf(this.active));
      this._changed({ flash: this.active });
    },

    /* ---------------- UI ---------------- */
    _build: function () {
      var self = this;
      this.root.textContent = '';
      this.root.classList.add('amc');
      this.live = h('p', { class: 'amc-sr', 'aria-live': 'polite' });
      var n = this.T.petCount;
      this.tabs = h('div', { class: 'amc-tabs', role: 'tablist', 'aria-label': 'Pets on this mat' });
      this.editor = h('div', { class: 'amc-editor', role: 'tabpanel' });
      this.root.appendChild(h('section', { class: 'amc-step', 'aria-labelledby': 'amc-h-pets' }, [
        h('div', { class: 'amc-step-head' }, [
          h('h2', { id: 'amc-h-pets', text: n === 1 ? 'Your pet' : 'Your ' + n + ' pets' }),
          h('span', { class: 'amc-hint', text: n === 1 ? 'This design fits 1 pet' : 'This design fits ' + n + ' pets' })
        ]),
        n > 1 ? this.tabs : null,
        this.editor
      ]));
      this.fieldsBox = h('div', { class: 'amc-fields' });
      if ((this.T.fields || []).length) {
        this.root.appendChild(h('section', { class: 'amc-step', 'aria-labelledby': 'amc-h-mat' }, [
          h('div', { class: 'amc-step-head' }, [h('h2', { id: 'amc-h-mat', text: 'Mat text' })]),
          this.fieldsBox
        ]));
      }
      this.errorBox = h('div', { class: 'amc-errors', role: 'alert', hidden: true });
      this.addBtn = h('button', { type: 'button', class: 'amc-add', 'data-amc': 'add', onclick: function () { self.addToCart(); } }, [
        h('span', { class: 'amc-add-label', text: this.o.buttonLabel || 'Add to cart' }),
        h('span', { class: 'amc-add-price', text: this.o.priceText != null ? this.o.priceText : (this.T.product.price || '') })
      ]);
      if (this.o.available === false) { this.addBtn.disabled = true; this.addBtn.firstChild.textContent = 'Sold out'; }
      this.summary = h('p', { class: 'amc-summary' });
      this.root.appendChild(h('div', { class: 'amc-cta' }, [this.errorBox, this.summary, this.addBtn]));
      this.root.appendChild(this.live);
      this._renderEditor();
      this._renderFields();
    },

    _renderFields: function () {
      var self = this, box = this.fieldsBox;
      box.textContent = '';
      (this.T.fields || []).forEach(function (f) {
        var id = 'amc-f-' + f.id, help = h('p', { class: 'amc-help', id: id + '-help' });
        function setHelp() {
          var v = D.clean(self.store.fields[f.id]);
          help.textContent = v ? f.help.replace('{value}', v).replace('{VALUE}', v.toUpperCase()) : 'Optional — leave blank to skip this line.';
        }
        var input = h('input', { id: id, type: 'text', class: 'amc-input', maxlength: String(f.max), placeholder: f.placeholder,
          value: self.store.fields[f.id] || '', autocomplete: 'off', 'aria-describedby': id + '-help', 'data-amc': 'field-' + f.id,
          oninput: function (e) { self.store.fields[f.id] = e.target.value; setHelp(); self._clearError(); self._changed(); } });
        setHelp();
        box.appendChild(h('div', { class: 'amc-field' }, [
          h('label', { class: 'amc-label', for: id, text: f.label + (f.required ? '' : ' (optional)') }), input, help
        ]));
      });
    },

    _renderTabs: function () {
      var self = this, n = this.T.petCount;
      if (n < 2) return;
      this.tabs.textContent = '';
      for (var i = 0; i < n; i++) (function (i) {
        var p = self.store.pets[i];
        self.tabs.appendChild(h('button', { type: 'button', role: 'tab', class: 'amc-tab', id: 'amc-tab-' + i,
          'aria-selected': i === self.active ? 'true' : 'false', tabindex: i === self.active ? '0' : '-1',
          onclick: function () { self.selectPet(i); },
          onkeydown: function (e) {
            var d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
            if (!d) return;
            e.preventDefault();
            var k = (i + d + n) % n; self.selectPet(k); self._focus('#amc-tab-' + k);
          } }, [
          h('img', { src: self.lib.src(self.o.assetBase, self.store.assetOf(i)), alt: '', class: 'amc-tab-img', width: '40', height: '40' }),
          h('span', { class: 'amc-tab-text' }, [
            h('span', { class: 'amc-tab-num', text: 'Pet ' + (i + 1) }),
            h('span', { class: 'amc-tab-name', text: D.clean(p.name) || 'Pet ' + (i + 1) })
          ])
        ]));
      })(i);
    },

    _renderEditor: function () {
      var self = this, i = this.active, p = this.pet(), lib = this.lib, base = this.o.assetBase, n = this.T.petCount;
      var ed = this.editor;
      ed.textContent = '';
      if (n > 1) ed.setAttribute('aria-labelledby', 'amc-tab-' + i);

      var spGroup = h('div', { class: 'amc-seg', role: 'radiogroup', 'aria-label': 'Dog or cat' });
      lib.catalog.species.forEach(function (sp) {
        var id = uid('sp');
        var sample = sp.id === 'cat' ? lib.breed('cat', 'domestic-cat').variants[0].asset : lib.breed('dog', 'labrador-retriever').variants[0].asset;
        spGroup.appendChild(h('label', { class: 'amc-seg-opt', for: id }, [
          h('input', { type: 'radio', id: id, name: 'amc-species', value: sp.id, checked: p.species === sp.id, onchange: function () { self.setSpecies(sp.id); } }),
          h('img', { src: lib.src(base, sample), alt: '', width: '34', height: '34' }),
          h('span', { text: sp.label })
        ]));
      });

      var br = lib.breed(p.species, p.breed), gridId = uid('breeds');
      var toggle = h('button', { type: 'button', class: 'amc-breed-current', 'data-amc': 'breed-toggle', 'aria-expanded': this.breedOpen ? 'true' : 'false', 'aria-controls': gridId,
        onclick: function () { self.breedOpen = !self.breedOpen; self._renderEditor(); self._focus('[data-amc="breed-toggle"]'); } }, [
        h('img', { src: lib.src(base, this.store.assetOf(i)), alt: '', width: '48', height: '48' }),
        h('span', { class: 'amc-breed-current-text' }, [h('span', { class: 'amc-label-sm', text: 'Breed' }), h('strong', { text: br.label })]),
        h('span', { class: 'amc-change', text: this.breedOpen ? 'Close' : 'Change' })
      ]);
      var grid = h('div', { class: 'amc-breeds', id: gridId, role: 'radiogroup', 'aria-label': 'Breed', hidden: !this.breedOpen });
      lib.species(p.species).breeds.forEach(function (b) {
        var id = uid('br');
        grid.appendChild(h('label', { class: 'amc-breed', for: id }, [
          h('input', { type: 'radio', id: id, name: 'amc-breed', value: b.id, checked: b.id === p.breed, onchange: function () { self.setBreed(b.id); } }),
          h('img', { src: lib.src(base, b.variants[0].asset), alt: '', loading: 'lazy', width: '72', height: '72' }),
          h('span', { class: 'amc-breed-name', text: b.label }),
          b.variants.length > 1 ? h('span', { class: 'amc-breed-count', text: b.variants.length + ' colors' }) : null
        ]));
      });

      var vGroup = h('div', { class: 'amc-variants', role: 'radiogroup', 'aria-label': 'Color' });
      br.variants.forEach(function (v) {
        var id = uid('v');
        vGroup.appendChild(h('label', { class: 'amc-variant', for: id }, [
          h('input', { type: 'radio', id: id, name: 'amc-variant', value: v.id, checked: v.id === p.variant, onchange: function () { self.setVariant(v.id); } }),
          h('img', { src: lib.src(base, v.asset), alt: '', width: '64', height: '64' }),
          h('span', { text: v.label })
        ]));
      });

      var max = this.T.nameMax || 12;
      var count = h('span', { id: 'amc-name-count', class: 'amc-count', text: Array.from(p.name).length + '/' + max });
      var nameInput = h('input', { id: 'amc-name', type: 'text', class: 'amc-input amc-input-name', 'data-amc': 'name',
        maxlength: String(max), autocomplete: 'off', autocapitalize: 'words', spellcheck: 'false',
        placeholder: 'e.g. ' + (['Max', 'Luna', 'Ringo', 'Coco'][i] || 'Coco'), value: p.name, 'aria-describedby': 'amc-name-count amc-name-err',
        oninput: function (e) {
          p.name = e.target.value;
          count.textContent = Array.from(e.target.value).length + '/' + max;
          self._clearError();
          self._changed();
        } });
      this.nameErr = h('p', { id: 'amc-name-err', class: 'amc-field-error', hidden: true });

      var actions = n > 1 ? h('div', { class: 'amc-actions' }, [
        h('button', { type: 'button', class: 'amc-link', 'data-amc': 'move-left', disabled: i === 0, onclick: function () { self.movePet(i, -1); } }, ['← Move left']),
        h('button', { type: 'button', class: 'amc-link', 'data-amc': 'move-right', disabled: i === n - 1, onclick: function () { self.movePet(i, 1); } }, ['Move right →'])
      ]) : null;

      ed.appendChild(h('div', { class: 'amc-field' }, [h('p', { class: 'amc-label', text: 'Pet type' }), spGroup]));
      ed.appendChild(h('div', { class: 'amc-field' }, [toggle, grid]));
      ed.appendChild(h('div', { class: 'amc-field' }, [h('p', { class: 'amc-label', text: br.variants.length > 1 ? 'Color' : 'Color (only one for this breed)' }), vGroup]));
      ed.appendChild(h('div', { class: 'amc-field' }, [
        h('div', { class: 'amc-label-row' }, [h('label', { class: 'amc-label', for: 'amc-name', text: 'Pet name' }), count]),
        nameInput, this.nameErr
      ]));
      if (actions) ed.appendChild(actions);
    },

    _clearError: function () {
      if (this.nameErr) { this.nameErr.hidden = true; this.nameErr.textContent = ''; }
      var inp = this.editor.querySelector('[data-amc="name"]');
      if (inp) inp.removeAttribute('aria-invalid');
      this.errorBox.hidden = true;
    },

    refresh: function () {
      var self = this, T = this.T;
      this._renderTabs();
      var parts = [];
      for (var i = 0; i < T.petCount; i++) {
        var d = this.lib.describe(this.store.assetOf(i));
        parts.push((D.clean(this.store.pets[i].name) || 'unnamed') + ' (' + d.breed + ', ' + d.color + ')');
      }
      this.summary.textContent = T.petCount + (T.petCount === 1 ? ' pet · ' : ' pets · ') + T.product.title;
      if (this.canvas) this.canvas.setAttribute('aria-label', T.product.title + ' preview with ' + parts.join(', ') + '.');
      var ids = []; for (var k = 0; k < T.petCount; k++) ids.push(this.store.assetOf(k));
      if (T.libAssets) this.lib.extra = Object.assign(this.lib.extra || {}, T.libAssets);
      this.images.ensure(ids.concat(D.templateAssets ? D.templateAssets(T) : []));
      this._schedule();
      this.listeners.forEach(function (fn) { fn(self); });
    },

    _bindPreview: function () {
      var self = this, c = this.canvas;
      if (!c) return;
      if (root.ResizeObserver) { this._ro = new ResizeObserver(function () { self._schedule(); }); this._ro.observe(c); }
      this._click = function (e) {
        if (!self.layout) return;
        var r = c.getBoundingClientRect(), vb = R.viewBox(self.T, 'mockup');
        var x = vb.x + (e.clientX - r.left) / r.width * vb.w, y = vb.y + (e.clientY - r.top) / r.height * vb.h;
        self.layout.pets.forEach(function (p) {
          if (x >= p.x && x <= p.x + p.w && y >= p.y && y <= p.y + p.h + 500) self.selectPet(p.index);
        });
      };
      c.addEventListener('click', this._click);
    },
    _schedule: function () {
      var self = this;
      if (this._raf || !this.canvas) return;
      this._raf = requestAnimationFrame(function () {
        self._raf = null;
        self.layout = Preview.draw(self.canvas, self.T, self.getDesign(), self.lib.catalog, self.images,
          { placeholderName: 'Pet name', highlight: self.highlight, highlightColor: self.T.ink });
      });
    },
    _flash: function (i) {
      var self = this;
      if (this.T.petCount < 2) return;
      this.highlight = i;
      clearTimeout(this._ft);
      this._ft = setTimeout(function () { self.highlight = null; self._schedule(); }, 1100);
      this._schedule();
    },

    addToCart: function () {
      var self = this, design = this.getDesign(), res = this.validate();
      if (!res.ok) {
        var e = res.errors[0], m = /^pets\[(\d)\]/.exec(e.path);
        this.errorBox.hidden = false; this.errorBox.textContent = e.message;
        if (m) {
          var idx = +m[1];
          if (idx !== this.active) this.selectPet(idx);
          this.nameErr.hidden = false; this.nameErr.textContent = e.message;
          var inp = this.editor.querySelector('[data-amc="name"]');
          if (inp) { inp.setAttribute('aria-invalid', 'true'); inp.focus(); }
        } else {
          var fid = e.path.split('.')[1], fi = this.root.querySelector('[data-amc="field-' + fid + '"]');
          if (fi) fi.focus();
        }
        return Promise.resolve(null);
      }
      this.addBtn.disabled = true; this.addBtn.classList.add('is-busy');
      return D.designKey(design).then(function (key) {
        var payload = { items: [{ id: self.o.variantId || 0, quantity: 1, properties: D.lineItemProperties(design, self.T, self.lib.catalog, key) }] };
        return Promise.resolve(self.o.cart.add(payload, self)).then(function (r) { self._announce('Added to cart.'); return { key: key, payload: payload, response: r }; });
      }).finally(function () { self.addBtn.disabled = false; self.addBtn.classList.remove('is-busy'); });
    },

    thumbnail: function (width) {
      var T = this.T, vb = R.viewBox(T, 'mockup'), c = makeCanvas(width || 720, Math.round((width || 720) * vb.h / vb.w));
      R.render(c.getContext('2d'), T, this.getDesign(), this.lib.catalog, { scale: c.width / vb.w, mode: 'mockup', images: this.images.map, createCanvas: makeCanvas });
      return c.toDataURL('image/png');
    },
    renderPrint: function () {
      var T = this.T, c = makeCanvas(T.size.w, T.size.h);
      R.render(c.getContext('2d'), T, this.getDesign(), this.lib.catalog, { scale: 1, mode: 'print', images: this.images.map, createCanvas: makeCanvas });
      return c;
    },
    destroy: function () {
      this.store.subs = this.store.subs.filter(function (f) { return f !== this._onStore; }, this);
      this.images.subs = this.images.subs.filter(function (f) { return f !== this._onImg; }, this);
      if (this._ro) this._ro.disconnect();
      if (this.canvas && this._click) this.canvas.removeEventListener('click', this._click);
      this.root.textContent = '';
    },
    _focus: function (sel) { var self = this; requestAnimationFrame(function () { var el = self.root.querySelector(sel); if (el) el.focus(); }); },
    _announce: function (msg) { var l = this.live; l.textContent = ''; setTimeout(function () { l.textContent = msg; }, 30); }
  };

  root.AmloveLib = Lib;
  root.AmloveStore = Store;
  root.AmloveImages = Images;
  root.AmlovePreview = Preview;
  root.AmloveCustomizer = { mount: function (el, o) { return new Customizer(el, o); }, STORE_KEY: STORE_KEY };
})(typeof self !== 'undefined' ? self : this);

;
window.AMLOVE_CATALOG = {"catalogVersion":"2026-10-28.v1","note":"Provisional reference art cut from galeria_aprobada.png. Replace each asset entry with the final transparent PNG/WebP (new @v1 id) without touching the UI.","pxScale":4.1,"species":[{"id":"dog","label":"Dog","breeds":[{"id":"australian-shepherd","label":"Australian Shepherd","variants":[{"id":"blue-merle","label":"Blue Merle","asset":"dog.australian-shepherd.blue-merle@ref1"}]},{"id":"beagle","label":"Beagle","variants":[{"id":"tricolor","label":"Tricolor","asset":"dog.beagle.tricolor@ref1"}]},{"id":"chihuahua","label":"Chihuahua","variants":[{"id":"cream","label":"Cream","asset":"dog.chihuahua.cream@ref1"},{"id":"chocolate","label":"Chocolate","asset":"dog.chihuahua.chocolate@ref1"}]},{"id":"corgi","label":"Corgi","variants":[{"id":"red-white","label":"Red & White","asset":"dog.corgi.red-white@ref1"}]},{"id":"dachshund","label":"Dachshund","variants":[{"id":"black-tan","label":"Black & Tan","asset":"dog.dachshund.black-tan@ref1"},{"id":"chocolate","label":"Chocolate","asset":"dog.dachshund.chocolate@ref1"},{"id":"cream","label":"Cream","asset":"dog.dachshund.cream@ref1"}]},{"id":"english-bulldog","label":"English Bulldog","variants":[{"id":"fawn-white","label":"Fawn & White","asset":"dog.english-bulldog.fawn-white@ref1"}]},{"id":"french-bulldog","label":"French Bulldog","variants":[{"id":"fawn","label":"Fawn","asset":"dog.french-bulldog.fawn@ref1"},{"id":"black","label":"Black","asset":"dog.french-bulldog.black@v1"},{"id":"brindle","label":"Brindle","asset":"dog.french-bulldog.brindle@ref1"}]},{"id":"german-shepherd","label":"German Shepherd","variants":[{"id":"black-tan","label":"Black & Tan","asset":"dog.german-shepherd.black-tan@ref1"}]},{"id":"golden-retriever","label":"Golden Retriever","variants":[{"id":"golden","label":"Golden","asset":"dog.golden-retriever.golden@v1"},{"id":"cream","label":"Cream","asset":"dog.golden-retriever.cream@ref1"}]},{"id":"labrador-retriever","label":"Labrador Retriever","variants":[{"id":"yellow","label":"Yellow","asset":"dog.labrador-retriever.yellow@ref1"},{"id":"black","label":"Black","asset":"dog.labrador-retriever.black@ref1"},{"id":"chocolate","label":"Chocolate","asset":"dog.labrador-retriever.chocolate@ref1"}]},{"id":"maltese","label":"Maltese","variants":[{"id":"white","label":"White","asset":"dog.maltese.white@ref1"}]},{"id":"mixed-breed","label":"Mixed Breed","variants":[{"id":"floppy-ears","label":"Floppy Ears","asset":"dog.mixed-breed.floppy-ears@ref1"},{"id":"scruffy","label":"Scruffy","asset":"dog.mixed-breed.scruffy@ref1"}]},{"id":"poodle","label":"Poodle","variants":[{"id":"white","label":"White","asset":"dog.poodle.white@ref1"},{"id":"brown","label":"Brown","asset":"dog.poodle.brown@ref1"},{"id":"black","label":"Black","asset":"dog.poodle.black@ref1"}]},{"id":"rottweiler","label":"Rottweiler","variants":[{"id":"black-tan","label":"Black & Tan","asset":"dog.rottweiler.black-tan@ref1"}]},{"id":"shih-tzu","label":"Shih Tzu","variants":[{"id":"brown-white","label":"Brown & White","asset":"dog.shih-tzu.brown-white@ref1"}]},{"id":"yorkshire-terrier","label":"Yorkshire Terrier","variants":[{"id":"black-tan","label":"Black & Tan","asset":"dog.yorkshire-terrier.black-tan@ref1"}]}]},{"id":"cat","label":"Cat","breeds":[{"id":"domestic-cat","label":"Domestic Cat","variants":[{"id":"gray-tabby","label":"Gray Tabby","asset":"cat.domestic-cat.gray-tabby@ref1"},{"id":"orange-tabby","label":"Orange Tabby","asset":"cat.domestic-cat.orange-tabby@ref1"},{"id":"black","label":"Black","asset":"cat.domestic-cat.black@ref1"},{"id":"white","label":"White","asset":"cat.domestic-cat.white@ref1"},{"id":"gray","label":"Gray","asset":"cat.domestic-cat.gray@ref1"},{"id":"black-white","label":"Black & White","asset":"cat.domestic-cat.black-white@ref1"},{"id":"calico","label":"Calico","asset":"cat.domestic-cat.calico@v1"}]},{"id":"maine-coon","label":"Maine Coon","variants":[{"id":"gray-tabby","label":"Gray Tabby","asset":"cat.maine-coon.gray-tabby@ref1"}]},{"id":"ragdoll","label":"Ragdoll","variants":[{"id":"colorpoint","label":"Colorpoint","asset":"cat.ragdoll.colorpoint@ref1"}]},{"id":"siamese","label":"Siamese","variants":[{"id":"seal-point","label":"Seal Point","asset":"cat.siamese.seal-point@ref1"}]}]}],"assets":{"dog.australian-shepherd.blue-merle@ref1":{"file":"amlove-pet-dog-australian-shepherd-blue-merle.webp","w":312,"h":286,"status":"provisional-reference"},"dog.beagle.tricolor@ref1":{"file":"amlove-pet-dog-beagle-tricolor.webp","w":332,"h":288,"status":"provisional-reference"},"dog.chihuahua.cream@ref1":{"file":"amlove-pet-dog-chihuahua-cream.webp","w":236,"h":276,"status":"provisional-reference"},"dog.chihuahua.chocolate@ref1":{"file":"amlove-pet-dog-chihuahua-chocolate.webp","w":246,"h":294,"status":"provisional-reference"},"dog.corgi.red-white@ref1":{"file":"amlove-pet-dog-corgi-red-white.webp","w":276,"h":294,"status":"provisional-reference"},"dog.dachshund.black-tan@ref1":{"file":"amlove-pet-dog-dachshund-black-tan.webp","w":332,"h":270,"status":"provisional-reference"},"dog.dachshund.chocolate@ref1":{"file":"amlove-pet-dog-dachshund-chocolate.webp","w":332,"h":272,"status":"provisional-reference"},"dog.dachshund.cream@ref1":{"file":"amlove-pet-dog-dachshund-cream.webp","w":316,"h":262,"status":"provisional-reference"},"dog.english-bulldog.fawn-white@ref1":{"file":"amlove-pet-dog-english-bulldog-fawn-white.webp","w":306,"h":272,"status":"provisional-reference"},"dog.french-bulldog.fawn@ref1":{"file":"amlove-pet-dog-french-bulldog-fawn.webp","w":302,"h":322,"status":"provisional-reference"},"dog.french-bulldog.black@ref1":{"file":"amlove-pet-dog-french-bulldog-black.webp","w":294,"h":322,"status":"provisional-reference"},"dog.french-bulldog.brindle@ref1":{"file":"amlove-pet-dog-french-bulldog-brindle.webp","w":296,"h":322,"status":"provisional-reference"},"dog.german-shepherd.black-tan@ref1":{"file":"amlove-pet-dog-german-shepherd-black-tan.webp","w":280,"h":340,"status":"provisional-reference"},"dog.golden-retriever.golden@ref1":{"file":"amlove-pet-dog-golden-retriever-golden.webp","w":376,"h":320,"status":"provisional-reference"},"dog.golden-retriever.cream@ref1":{"file":"amlove-pet-dog-golden-retriever-cream.webp","w":362,"h":310,"status":"provisional-reference"},"dog.labrador-retriever.yellow@ref1":{"file":"amlove-pet-dog-labrador-retriever-yellow.webp","w":360,"h":296,"status":"provisional-reference"},"dog.labrador-retriever.black@ref1":{"file":"amlove-pet-dog-labrador-retriever-black.webp","w":354,"h":294,"status":"provisional-reference"},"dog.labrador-retriever.chocolate@ref1":{"file":"amlove-pet-dog-labrador-retriever-chocolate.webp","w":358,"h":304,"status":"provisional-reference"},"dog.maltese.white@ref1":{"file":"amlove-pet-dog-maltese-white.webp","w":272,"h":258,"status":"provisional-reference"},"dog.mixed-breed.floppy-ears@ref1":{"file":"amlove-pet-dog-mixed-breed-floppy-ears.webp","w":316,"h":268,"status":"provisional-reference"},"dog.mixed-breed.scruffy@ref1":{"file":"amlove-pet-dog-mixed-breed-scruffy.webp","w":312,"h":274,"status":"provisional-reference"},"dog.poodle.white@ref1":{"file":"amlove-pet-dog-poodle-white.webp","w":328,"h":304,"status":"provisional-reference"},"dog.poodle.brown@ref1":{"file":"amlove-pet-dog-poodle-brown.webp","w":322,"h":312,"status":"provisional-reference"},"dog.poodle.black@ref1":{"file":"amlove-pet-dog-poodle-black.webp","w":320,"h":314,"status":"provisional-reference"},"dog.rottweiler.black-tan@ref1":{"file":"amlove-pet-dog-rottweiler-black-tan.webp","w":314,"h":294,"status":"provisional-reference"},"dog.shih-tzu.brown-white@ref1":{"file":"amlove-pet-dog-shih-tzu-brown-white.webp","w":304,"h":260,"status":"provisional-reference"},"dog.yorkshire-terrier.black-tan@ref1":{"file":"amlove-pet-dog-yorkshire-terrier-black-tan.webp","w":236,"h":284,"status":"provisional-reference"},"cat.domestic-cat.gray-tabby@ref1":{"file":"amlove-pet-cat-domestic-cat-gray-tabby.webp","w":260,"h":280,"status":"provisional-reference"},"cat.domestic-cat.orange-tabby@ref1":{"file":"amlove-pet-cat-domestic-cat-orange-tabby.webp","w":246,"h":264,"status":"provisional-reference"},"cat.domestic-cat.black@ref1":{"file":"amlove-pet-cat-domestic-cat-black.webp","w":258,"h":272,"status":"provisional-reference"},"cat.domestic-cat.white@ref1":{"file":"amlove-pet-cat-domestic-cat-white.webp","w":250,"h":266,"status":"provisional-reference"},"cat.domestic-cat.gray@ref1":{"file":"amlove-pet-cat-domestic-cat-gray.webp","w":248,"h":264,"status":"provisional-reference"},"cat.domestic-cat.black-white@ref1":{"file":"amlove-pet-cat-domestic-cat-black-white.webp","w":258,"h":250,"status":"provisional-reference"},"cat.domestic-cat.calico@ref1":{"file":"amlove-pet-cat-domestic-cat-calico.webp","w":260,"h":268,"status":"provisional-reference"},"cat.maine-coon.gray-tabby@ref1":{"file":"amlove-pet-cat-maine-coon-gray-tabby.webp","w":302,"h":304,"status":"provisional-reference"},"cat.ragdoll.colorpoint@ref1":{"file":"amlove-pet-cat-ragdoll-colorpoint.webp","w":290,"h":276,"status":"provisional-reference"},"cat.siamese.seal-point@ref1":{"file":"amlove-pet-cat-siamese-seal-point.webp","w":242,"h":282,"status":"provisional-reference"},"dog.golden-retriever.golden@v1":{"file":"amlove-pet-dog-golden-retriever-golden-v1.webp","w":326,"h":300,"sink":0.0152,"core":[0,0.9916],"status":"final-warn"},"dog.french-bulldog.black@v1":{"file":"amlove-pet-dog-french-bulldog-black-v1.webp","w":266,"h":300,"sink":0.015,"core":[0,0.9989],"status":"final-warn"},"cat.domestic-cat.calico@v1":{"file":"amlove-pet-cat-domestic-cat-calico-v1.webp","w":336,"h":300,"sink":0.0187,"core":[0.0936,0.8462],"status":"final-warn"}}};
;
/*!
 * Amlove · Customizer block — boots the shared customizer on a Shopify product page.
 * Design source: the designs file of the theme (amlove-templates.json), chosen by product tag or handle.
 * Add to cart: Shopify Ajax Cart API with line item properties, then tells the Horizon cart to refresh.
 */
(function () {
  'use strict';
  var DEFAULT_PETS = [
    { species: 'dog', breed: 'golden-retriever', variant: 'golden', name: 'Max' },
    { species: 'cat', breed: 'domestic-cat', variant: 'calico', name: 'Luna' },
    { species: 'dog', breed: 'french-bulldog', variant: 'black', name: 'Bella' },
    { species: 'dog', breed: 'poodle', variant: 'white', name: 'Coco' }
  ];
  var cache = {};
  function getJSON(url) { return cache[url] || (cache[url] = fetch(url, { credentials: 'omit' }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })); }
  function baseOf(url) { var u = url.split('?')[0]; return u.slice(0, u.lastIndexOf('/') + 1); }

  function fail(root, msg) {
    var box = root.querySelector('[data-amlove-root]');
    box.textContent = '';
    var p = document.createElement('p'); p.className = 'amlove-error'; p.setAttribute('role', 'alert'); p.textContent = msg; box.appendChild(p);
  }

  // Which design this product shows: Studio link (when present) → product tag "amlove-<design id>" →
  // the block setting → the product handle. Among several revisions, the newest approved one wins.
  function pickTemplate(root, tdoc) {
    var raw = root.querySelector('[data-amlove-template]'), T = null;
    try { T = JSON.parse(raw && raw.textContent || 'null'); } catch (e) { T = null; }
    if (T && T.layers) return T;
    var list = ((tdoc && tdoc.templates) || []).filter(function (t) { return t.status === 'approved'; });
    var tags = (root.getAttribute('data-tags') || '').split(',').map(function (x) { return x.trim().toLowerCase(); });
    var want = root.getAttribute('data-fallback'), handle = root.getAttribute('data-product');
    function newest(fn) { return list.filter(fn).sort(function (a, b) { return b.rev - a.rev; })[0]; }
    T = newest(function (t) { return tags.indexOf('amlove-' + t.id) >= 0; }) ||
        (want && want !== 'auto' ? newest(function (t) { return t.id === want; }) : null) ||
        newest(function (t) { return t.product.handle === handle; });
    if (T) root.setAttribute('data-source', 'theme');
    return T || null;
  }

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) { if (ids.indexOf(el.dataset.sectionId) < 0) ids.push(el.dataset.sectionId); });
    return ids;
  }

  function makeCart(root) {
    return {
      add: function (payload, customizer) {
        var ids = sectionIds(), body = { items: payload.items };
        if (ids.length) body.sections = ids.join(',');
        var addUrl = (root.getAttribute('data-cart-add') || '/cart/add') + '.js';
        return fetch(addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) })
          .then(function (r) { return r.json().then(function (j) { if (!r.ok || j.status) throw new Error(j.description || j.message || 'Could not add to cart.'); return j; }); })
          .then(function (added) {
            var cartUrl = (root.getAttribute('data-cart-url') || '/cart') + '.js';
            return fetch(cartUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).catch(function () { return null; }).then(function (cart) {
              var after = root.getAttribute('data-after') || 'drawer';
              document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {
                resource: cart || undefined, sourceId: root.id,
                data: { source: 'amlove-customizer', itemCount: 1, productId: root.getAttribute('data-product-id'), variantId: String(payload.items[0].id), sections: added.sections }
              } }));
              var drawer = document.querySelector('cart-drawer-component');
              if (after === 'cart' || (after === 'drawer' && !drawer)) { window.location.href = root.getAttribute('data-cart-url') || '/cart'; return added; }
              if (after === 'drawer' && drawer && typeof drawer.open === 'function' && !drawer.hasAttribute('auto-open')) drawer.open();
              return added;
            });
          })
          .catch(function (e) {
            if (customizer && customizer.errorBox) { customizer.errorBox.hidden = false; customizer.errorBox.textContent = e.message; }
            throw e;
          });
      }
    };
  }

  function boot(root) {
    if (root.__amlove) return; root.__amlove = true;
    if (!window.AmloveRenderer || !window.AmloveDesign || !window.AmloveCustomizer) return fail(root, 'The designer could not load. Please refresh the page.');
    var catUrl = root.getAttribute('data-catalog');
    var catP = window.AMLOVE_CATALOG ? Promise.resolve(window.AMLOVE_CATALOG) : getJSON(catUrl);   // the bundle carries the catalog
    Promise.all([catP, getJSON(root.getAttribute('data-templates')).catch(function () { return null; })]).then(function (res) {
      var catalog = res[0], T = pickTemplate(root, res[1]);
      if (!T) return fail(root, 'This design is not available right now.');
      if (T.status !== 'approved') return fail(root, 'This design is being updated. Please check back soon.');
      var base = root.getAttribute('data-asset-base') ? baseOf(root.getAttribute('data-asset-base')) : baseOf(catUrl);   // pet pictures live in Shopify Files
      var lib = new window.AmloveLib(catalog), images = new window.AmloveImages(lib, base);
      var store = new window.AmloveStore(lib, { pets: DEFAULT_PETS, fields: { family: '' } });
      var mountEl = root.querySelector('[data-amlove-root]'); mountEl.textContent = '';
      var preview = root.querySelector('[data-amlove-preview]');
      var c = window.AmloveCustomizer.mount(mountEl, {
        template: T, templateIndex: (function () { var x = {}; x[T.id + '@' + T.rev] = T; return x; })(), lib: lib, store: store, images: images, assetBase: base, previewCanvas: preview,
        cart: makeCart(root), variantId: Number(root.getAttribute('data-variant')),
        priceText: root.getAttribute('data-price') || '', buttonLabel: root.getAttribute('data-button') || 'Add to cart',
        available: root.getAttribute('data-available') !== 'false'
      });
      root.__amloveCustomizer = c;
      if (document.fonts) {
        document.fonts.ready.then(function () { c.refresh(); });
        if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', function () { c.refresh(); });
      }
      root.setAttribute('data-ready', 'true');
      if (/[?&]amlove_debug=1/.test(location.search)) {   // installation check: which design, which revision, from where
        var tag = document.createElement('p'); tag.className = 'amlove-debug';
        tag.textContent = 'Amlove · ' + T.id + '@' + T.rev + ' · origen: ' + (root.getAttribute('data-source') === 'shopify' ? 'Amlove Studio (Shopify)' : 'diseños del tema') + ' · variante ' + root.getAttribute('data-variant');
        root.insertBefore(tag, root.firstChild);
      }
    }).catch(function (e) { console.error('[Amlove]', e); fail(root, 'The designer could not load. Please refresh the page.'); });
  }

  function start() { document.querySelectorAll('[data-amlove-block]').forEach(boot); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  // theme editor: re-boot when the block is added or moved
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:block:select', start);
})();
